import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useT } from '../i18n';

interface GameModeSelectorProps {
  /** Navigate to overall high level modes (only used for advanced fallback) */
  onModeSelect: (mode: 'single' | 'multi' | 'advanced' | 'online') => void;
  /** Direct variant selection for single player */
  onSelectSingleVariant: (variant: 'classic' | 'maxi' | 'forced-classic' | 'forced-maxi' | 'daily') => void;
  /** Direct variant selection for multiplayer (will open setup) */
  onSelectMultiplayerVariant: (variant: 'classic' | 'maxi' | 'forced-classic' | 'forced-maxi') => void;
  onHome?: () => void;
  /** Sparat spel som kan återupptas – visar "Fortsätt spel" som primär knapp. */
  resumeInfo?: { modeName: string; round: number } | null;
  onResume?: () => void;
  /** Utan sparat spel visas istället "Spela" (senaste läget) som primär knapp. */
  quickPlayModeName?: string | null;
  onQuickPlay?: () => void;
}

// 1. Keyframes for subtle staggered entrance
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

// 2. Theming & container styling using CSS vars (falls back if not defined)
const SelectorContainer = styled.div`
  background: var(--theme-surface, #fff);
  color: var(--theme-text, #222);
  border-radius: 20px;
  padding: 42px 46px 36px;
  box-shadow: 0 10px 28px -4px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.08);
  max-width: 560px;
  margin: 0 auto;
  text-align: center;
  backdrop-filter: var(--backdrop-blur, blur(8px));
  position: relative;
  overflow: hidden;
  --focus-ring: var(--focus-ring-color, #ffcf3f);
  --focus-outline-shadow: 0 0 0 3px var(--focus-ring), 0 0 0 6px rgba(0,0,0,0.45);
  
  @media (max-width: 560px) {
    padding: 32px 28px 28px;
    border-radius: 18px;
  }
  @media (max-width: 480px) {
    padding: 34px 24px 30px;
    border-radius: 16px;
    max-width: 95%;
  }
  @media (max-width: 420px) {
    padding: 26px 20px 24px;
  }
`;

// 3. Brand heading & mode heading semantics
const Brand = styled.h1`
  margin: 0 0 8px;
  font-size: clamp(40px, 9vw, 54px);
  letter-spacing: 0.18em;
  font-weight: 800;
  line-height: 1;
  color: var(--theme-text, #222);
  text-shadow: 0 3px 8px rgba(0,0,0,0.18);
  user-select: none;
  cursor: pointer;
  &:hover { text-shadow:0 4px 10px rgba(0,0,0,0.28); transform:translateY(-2px); }
  transition: all .3s ease;
`;

const Title = styled.h2`
  color: var(--theme-muted, color-mix(in srgb, var(--theme-text, #222) 70%, #888));
  font-size: clamp(22px, 5vw, 30px);
  margin: 0 0 32px;
  font-weight: 600;
  letter-spacing: .5px;
`;

// 4. Shared variant gradients
const variantGradients: Record<string, {normal: string; hover: string; text?: string}> = {
  classic: { normal: 'linear-gradient(145deg,#4CAF50,#2e7d32)', hover: 'linear-gradient(145deg,#2e7d32,#4CAF50)' },
  maxi: { normal: 'linear-gradient(145deg,#1976d2,#0d47a1)', hover: 'linear-gradient(145deg,#0d47a1,#1976d2)' },
  forced: { normal: 'linear-gradient(145deg,#8e24aa,#6a1b9a)', hover: 'linear-gradient(145deg,#6a1b9a,#8e24aa)' },
  forcedMaxi: { normal: 'linear-gradient(145deg,#ff6f00,#ff8f00)', hover: 'linear-gradient(145deg,#ff8f00,#ff6f00)' },
  online: { normal: 'linear-gradient(145deg,#e91e63,#ad1457)', hover: 'linear-gradient(145deg,#ad1457,#e91e63)' },
  advanced: { normal: 'linear-gradient(145deg,#455a64,#263238)', hover: 'linear-gradient(145deg,#263238,#455a64)' },
  daily: { normal: 'linear-gradient(145deg,#f59e0b,#b45309)', hover: 'linear-gradient(145deg,#b45309,#f59e0b)' }
};

interface VariantButtonProps { $variant: keyof typeof variantGradients; $index: number; }

const ModeButton = styled.button<VariantButtonProps>`
  ${({ $variant }) => css`background: ${variantGradients[$variant].normal};`}
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 18px 28px 18px;
  font-size: clamp(16px, 3.8vw, 19px);
  font-weight: 700;
  cursor: pointer;
  margin: 12px 12px 4px;
  min-width: 210px;
  position: relative;
  line-height: 1.15;
  letter-spacing: .5px;
  transition: transform .28s cubic-bezier(.16,.8,.28,1), box-shadow .28s ease, background .35s ease;
  box-shadow: 0 4px 10px rgba(0,0,0,.25), 0 1px 2px rgba(0,0,0,.18);
  animation: ${fadeSlideIn} .55s cubic-bezier(.16,.8,.28,1) both;
  animation-delay: ${({ $index }) => $index * 70}ms;
  will-change: transform;

  &:hover {
    ${({ $variant }) => css`background: ${variantGradients[$variant].hover};`}
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 8px 18px -4px rgba(0,0,0,.38), 0 2px 4px rgba(0,0,0,.22);
  }
  &:active {
    transform: translateY(0) scale(.98);
    transition-duration: .15s;
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-outline-shadow), 0 4px 10px rgba(0,0,0,.4);
  }
  @media (max-width: 480px) {
    padding: 20px 24px 20px;
    margin: 10px 8px 2px;
    min-width: 200px;
    font-size: 17px;
    border-radius: 16px;
  }
`;

// (Tidigare Description-komponent togs bort – ej använd)

// CHANGE (2026-08-15): Texterna hämtas nu från i18n (sv/en) via useT().
const BRAND = 'YATZY';

// CHANGE (2026-08-15): Primär CTA överst – "Fortsätt spel" om ett sparat spel
// finns, annars "Spela" (senaste singleplayer-läget). Ger startskärmen en
// tydlig hierarki: ett självklart förstahandsval, detaljvalen under.
const PrimaryPlayButton = styled.button`
  width: 100%;
  background: linear-gradient(145deg, #43a047, #1b5e20);
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 18px 24px;
  margin: 0 0 28px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  box-shadow: 0 6px 16px -4px rgba(0,0,0,.35), 0 2px 5px rgba(0,0,0,.2);
  transition: transform .25s cubic-bezier(.16,.8,.28,1), box-shadow .25s ease, filter .25s ease;
  animation: ${fadeSlideIn} .45s cubic-bezier(.16,.8,.28,1) both;

  & strong {
    font-size: clamp(19px, 4.6vw, 23px);
    font-weight: 800;
    letter-spacing: .5px;
  }
  & span {
    font-size: 13.5px;
    opacity: .92;
  }
  &:hover {
    filter: brightness(1.08);
    transform: translateY(-3px);
    box-shadow: 0 10px 22px -6px rgba(0,0,0,.45), 0 3px 7px rgba(0,0,0,.25);
  }
  &:active { transform: translateY(0) scale(.98); }
  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-outline-shadow), 0 6px 16px -4px rgba(0,0,0,.4);
  }
`;

// Section containers
const Section = styled.section`
  margin: 0 0 34px;
  &:last-of-type { margin-bottom: 0; }
`;
const SectionTitle = styled.h3`
  margin: 0 0 14px;
  font-size: clamp(18px,4.5vw,24px);
  font-weight: 700;
  letter-spacing: .5px;
  color: var(--theme-text,#222);
  
  @media (max-width: 480px) {
    font-size: 20px;
    margin: 0 0 16px;
  }
`;
const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit,minmax(180px,1fr));
  gap: 14px 18px;
  justify-items: center;
  margin-bottom: 10px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;
const SmallNote = styled.p`
  font-size: 12.5px;
  margin: 4px 0 0;
  opacity: .75;
  
  @media (max-width: 480px) {
    font-size: 13.5px;
    margin: 6px 0 0;
  }
`;

const GameModeSelector: React.FC<GameModeSelectorProps> = ({ onModeSelect, onSelectSingleVariant, onSelectMultiplayerVariant, onHome, resumeInfo, onResume, quickPlayModeName, onQuickPlay }) => {
  const t = useT();
  // IDs for aria-describedby association
  const modeHeadingId = 'mode-heading';

  return (
    <SelectorContainer role="group" aria-labelledby={modeHeadingId}>
  <Brand onClick={()=> onHome && onHome()} title="Hem / Start">{BRAND}</Brand>
      <Title id={modeHeadingId}>{t('start.chooseMode')}</Title>

      {resumeInfo && onResume ? (
        <PrimaryPlayButton onClick={onResume} aria-label={`${t('start.resume')}: ${resumeInfo.modeName}`}>
          <strong>{t('start.resume')}</strong>
          <span>{t('start.resumeSub', { mode: resumeInfo.modeName, round: resumeInfo.round })}</span>
        </PrimaryPlayButton>
      ) : quickPlayModeName && onQuickPlay ? (
        <PrimaryPlayButton onClick={onQuickPlay} aria-label={`${t('start.play')} ${quickPlayModeName}`}>
          <strong>{t('start.play')}</strong>
          <span>{quickPlayModeName}</span>
        </PrimaryPlayButton>
      ) : null}

      <Section aria-labelledby="daily-heading">
        <SectionTitle id="daily-heading">{t('start.daily.heading')}</SectionTitle>
        <ButtonGrid style={{gridTemplateColumns:'1fr'}}>
          <ModeButton $variant="daily" $index={0} onClick={() => onSelectSingleVariant('daily')} style={{width:'100%'}}>
            {t('start.daily.button')}
          </ModeButton>
        </ButtonGrid>
        <SmallNote>{t('start.daily.desc')}</SmallNote>
      </Section>

      <Section aria-labelledby="single-player-heading">
        <SectionTitle id="single-player-heading">{t('start.single.heading')}</SectionTitle>
        <ButtonGrid>
          <ModeButton $variant="classic" $index={1} onClick={() => onSelectSingleVariant('classic')}>{t('start.single.classic')}</ModeButton>
          <ModeButton $variant="maxi" $index={2} onClick={() => onSelectSingleVariant('maxi')}>{t('start.single.maxi')}</ModeButton>
          <ModeButton $variant="forced" $index={3} onClick={() => onSelectSingleVariant('forced-classic')}>{t('start.single.forced')}</ModeButton>
          <ModeButton $variant="forcedMaxi" $index={4} onClick={() => onSelectSingleVariant('forced-maxi')}>{t('start.single.forcedMaxi')}</ModeButton>
        </ButtonGrid>
        <SmallNote>{t('start.single.desc')}</SmallNote>
      </Section>

      <Section aria-labelledby="multi-player-heading">
        <SectionTitle id="multi-player-heading">{t('start.multi.heading')}</SectionTitle>
        <ButtonGrid style={{gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))'}}>
          <ModeButton $variant="classic" $index={4} onClick={() => onModeSelect('multi')}>{t('start.multi.local')}</ModeButton>
          <ModeButton
            $variant="online"
            $index={5}
            onClick={() => onModeSelect('online')}
          >
            {t('start.multi.online')}
          </ModeButton>
        </ButtonGrid>
        {/* CHANGE (2026-08-14): Texten gällde bara lokal multiplayer men stod under båda knapparna. */}
        <SmallNote>{t('start.multi.desc')}</SmallNote>
      </Section>

      <Section aria-labelledby="advanced-heading">
        <SectionTitle id="advanced-heading">{t('start.advanced.heading')}</SectionTitle>
        <ButtonGrid style={{gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))'}}>
          <ModeButton $variant="advanced" $index={8} onClick={() => onModeSelect('advanced')}>{t('start.advanced.button')}</ModeButton>
        </ButtonGrid>
      </Section>
    </SelectorContainer>
  );
}

export default GameModeSelector;
